import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCustomers, useBlockCustomer, useUnblockCustomer } from '../../hooks/customers/useCustomers';
import { Customer } from '../../api/types/customer.types';
import { format } from 'date-fns';
import CustomerRegistrationModal from './CustomerRegistrationModal';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'BLOCKED'>('ALL');
  const [page, setPage] = useState(1);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const { data, isLoading, error } = useCustomers({
    page,
    limit: 20,
    status: statusFilter,
    search: searchQuery,
  });

  const blockMutation = useBlockCustomer();
  const unblockMutation = useUnblockCustomer();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setMenuAnchor(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedCustomer(null);
  };

  const handleViewCustomer = () => {
    if (selectedCustomer) {
      navigate(`/customers/${selectedCustomer.customerId}`);
    }
    handleMenuClose();
  };

  const handleBlockCustomer = async () => {
    if (selectedCustomer && !selectedCustomer.isBlocked) {
      await blockMutation.mutateAsync({
        customerId: selectedCustomer.customerId,
        data: {
          reason: 'Blocked by admin',
          blockedBy: 'current-user-id', // Should come from auth context
        },
      });
    }
    handleMenuClose();
  };

  const handleUnblockCustomer = async () => {
    if (selectedCustomer && selectedCustomer.isBlocked) {
      await unblockMutation.mutateAsync(selectedCustomer.customerId);
    }
    handleMenuClose();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getKYCStatusColor = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Customers
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load customers. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Customers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage customer registrations and profiles
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRegistrationOpen(true)}
          size="large"
        >
          Register Customer
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search by name, phone, ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label="All"
            onClick={() => setStatusFilter('ALL')}
            color={statusFilter === 'ALL' ? 'primary' : 'default'}
            variant={statusFilter === 'ALL' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Active"
            onClick={() => setStatusFilter('ACTIVE')}
            color={statusFilter === 'ACTIVE' ? 'success' : 'default'}
            variant={statusFilter === 'ACTIVE' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Blocked"
            onClick={() => setStatusFilter('BLOCKED')}
            color={statusFilter === 'BLOCKED' ? 'error' : 'default'}
            variant={statusFilter === 'BLOCKED' ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Customer Name</strong></TableCell>
              <TableCell><strong>Phone Number</strong></TableCell>
              <TableCell><strong>ID Number</strong></TableCell>
              <TableCell><strong>KYC Status</strong></TableCell>
              <TableCell><strong>Credit Score</strong></TableCell>
              <TableCell><strong>Loan Limit</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Registered</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data?.customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No customers found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data?.customers.map((customer) => (
                <TableRow
                  key={customer.customerId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/customers/${customer.customerId}`)}
                >
                  <TableCell>{customer.fullName}</TableCell>
                  <TableCell>{customer.phoneNumber}</TableCell>
                  <TableCell>{customer.idNumber}</TableCell>
                  <TableCell>
                    <Chip
                      label={customer.kycStatus}
                      size="small"
                      color={getKYCStatusColor(customer.kycStatus) as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                    />
                  </TableCell>
                  <TableCell>{customer.creditScore}</TableCell>
                  <TableCell>{formatCurrency(customer.currentLoanLimit)}</TableCell>
                  <TableCell>
                    <Chip
                      label={customer.isBlocked ? 'Blocked' : 'Active'}
                      size="small"
                      color={customer.isBlocked ? 'error' : 'success'}
                      icon={customer.isBlocked ? <BlockIcon /> : <CheckCircleIcon />}
                    />
                  </TableCell>
                  <TableCell>
                    {customer.createdAt
                      ? format(new Date(customer.createdAt), 'MMM dd, yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, customer)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Info */}
      {data && data.pagination && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {data.customers?.length || 0} of {data.pagination.total} customers
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ px: 2, py: 1 }}>
              Page {page} of {data.pagination.pages}
            </Typography>
            <Button
              size="small"
              disabled={page === data.pagination.pages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewCustomer}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        {selectedCustomer?.isBlocked ? (
          <MenuItem onClick={handleUnblockCustomer}>
            <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
            Unblock Customer
          </MenuItem>
        ) : (
          <MenuItem onClick={handleBlockCustomer}>
            <BlockIcon sx={{ mr: 1 }} fontSize="small" />
            Block Customer
          </MenuItem>
        )}
      </Menu>

      {/* Registration Modal */}
      <CustomerRegistrationModal
        open={registrationOpen}
        onClose={() => setRegistrationOpen(false)}
      />
    </Box>
  );
};

export default Customers;
